// src/utilities/weightCalculations.js

import { useMemo } from 'react';
import {
  addWeeks,
  isValid,
  format,
  differenceInWeeks,
  startOfToday,
  startOfDay,
  isSameDay,
  isAfter,
} from 'date-fns';
import { utcStringToDate, utcToLocal } from './utilities';

export const DEFAULT_AVG_LOSS = 1.5; // lbs/week fallback

/**
 * Normalize input into a JS Date in local time.
 * - If already Date, return as-is.
 * - If string, parse as MySQL UTC then convert to local.
 * - Otherwise return null.
 */
function toDate(input) {
  if (input instanceof Date) return input;
  if (typeof input === 'string') {
    const utc = utcStringToDate(input);
    return utcToLocal(utc);
  }
  return null;
}

// ——— Pure Utilities ———

/** @returns BMI as string fixed to one decimal, or null */
export const calculateBMI = (heightInches, weightLbs) => {
  const h = parseFloat(heightInches),
    w = parseFloat(weightLbs);
  if (h > 0 && w > 0) {
    return ((w / (h * h)) * 703).toFixed(1);
  }
  return null;
};

export const calculateGoalWeight = (heightInches, targetBMI) => {
  const w = (targetBMI * heightInches * heightInches) / 703;
  return w.toFixed(1);
};

/**
 * @returns average lbs lost/week, or null if not enough data.
 */
export const calculateAverageWeeklyLoss = (startDateParam, historyData = []) => {
  const start = toDate(startDateParam);
  if (!start || !isValid(start)) return null;

  const beg = startOfDay(start);
  const rel = historyData
    .map(item => ({
      date: toDate(item.reported_date),
      weight:
        item.weight != null && !isNaN(parseFloat(item.weight))
          ? parseFloat(item.weight)
          : null,
    }))
    .filter(i => i.date && isValid(i.date) && i.weight != null)
    .filter(
      i =>
        isSameDay(startOfDay(i.date), beg) ||
        isAfter(startOfDay(i.date), beg)
    )
    .sort((a, b) => a.date - b.date);

  if (rel.length < 2) return null;
  const first = rel[0],
    last = rel[rel.length - 1];
  const weeks = differenceInWeeks(last.date, first.date);
  const lost = first.weight - last.weight;
  if (weeks <= 0 || lost <= 0) return 0;
  return parseFloat((lost / weeks).toFixed(2));
};

/**
 * @returns total lbs lost since startDateParam, or null if no data.
 */
export const calculateTotalWeightLoss = (startDateParam, historyData = []) => {
  const start = toDate(startDateParam);
  if (!start || !isValid(start)) return null;

  const rel = historyData
    .map(item => ({
      date: toDate(item.reported_date),
      weight:
        item.weight != null && !isNaN(parseFloat(item.weight))
          ? parseFloat(item.weight)
          : null,
    }))
    .filter(i => i.date && isValid(i.date) && i.weight != null && i.date >= start)
    .sort((a, b) => a.date - b.date);

  if (rel.length < 1) return null;
  const diff = rel[0].weight - rel[rel.length - 1].weight;
  return parseFloat(diff.toFixed(1));
};

/**
 * @returns YYYY-MM-DD string for projected goal date, or '' if impossible.
 */
export const calculateEstimatedGoalDate = (
  startDateParam,
  currentWeightParam,
  goalWeightParam,
  avgLossPerWeek = null,
  useTodayAsStart = false
) => {
  const avg = avgLossPerWeek > 0 ? avgLossPerWeek : DEFAULT_AVG_LOSS;
  const start = useTodayAsStart ? startOfToday() : toDate(startDateParam);
  const cw =
    currentWeightParam != null ? parseFloat(currentWeightParam) : null;
  const gw = goalWeightParam != null ? parseFloat(goalWeightParam) : null;

  if (start && isValid(start) && cw != null && gw != null) {
    const toLose = cw - gw;
    if (toLose <= 0) return format(new Date(), 'yyyy-MM-dd');
    const weeks = Math.ceil(toLose / avg);
    return format(addWeeks(start, weeks), 'yyyy-MM-dd');
  }
  return '';
};

// ——— Hook: combine metrics ———

/**
 * @param {Object} user        from userContext.user
 * @param {Array} orders       orderContext.orders (order_date: Date)
 * @param {Array} orderHistory orderContext.orderHistory (reported_date: Date)
 */
export function useWeightMetrics(
  user = {},
  orders = [],
  orderHistory = []
) {
  // 1) Build clean historyData
  const historyData = useMemo(
    () =>
      orderHistory
        .map(h => ({
          reported_date: toDate(h.reported_date),
          weight: h.weight,
        }))
        .filter(i => i.reported_date && i.weight != null),
    [orderHistory]
  );

  // 2) earliest order date
  const earliestOrderDate = useMemo(() => {
    const dates = orders
      .map(o => toDate(o.order_date))
      .filter(d => d && isValid(d))
      .sort((a, b) => a - b);
    return dates[0] || null;
  }, [orders]);

  // 3) effective startDate: user.start_date → earliestOrderDate → today
  const startDate = useMemo(() => {
    const u = toDate(user.start_date);
    if (u && isValid(u)) return u;
    if (earliestOrderDate) return earliestOrderDate;
    return startOfToday();
  }, [user.start_date, earliestOrderDate]);

  // 4) effective currentWeight: last order.weight → user.weight
  const currentWeight = useMemo(() => {
    if (orders.length) {
      const latest = orders
        .map(o => ({
          date: toDate(o.order_date),
          weight: o.weight != null ? parseFloat(o.weight) : null,
        }))
        .filter(i => i.date && isValid(i.date) && i.weight != null)
        .sort((a, b) => b.date - a.date)[0];
      if (latest) return latest.weight;
    }
    return user.weight != null ? parseFloat(user.weight) : 0;
  }, [orders, user.weight]);

  // 5) metrics
  const bmi = useMemo(
    () => calculateBMI(user.height, currentWeight),
    [user.height, currentWeight]
  );

  const averageLossPerWeek = useMemo(() => {
    const avg = calculateAverageWeeklyLoss(startDate, historyData);
    return avg != null ? avg : DEFAULT_AVG_LOSS;
  }, [startDate, historyData]);

  const totalWeightLost = useMemo(() => {
    const total = calculateTotalWeightLoss(startDate, historyData);
    return total != null ? total : 0;
  }, [startDate, historyData]);

  const projectedGoalDate = useMemo(
    () =>
      calculateEstimatedGoalDate(
        startDate,
        currentWeight,
        user.goal_weight,
        averageLossPerWeek,
        false
      ),
    [startDate, currentWeight, user.goal_weight, averageLossPerWeek]
  );

  return {
    bmi,
    averageLossPerWeek,
    totalWeightLost,
    projectedGoalDate,
    historyData,
    startDate,
    currentWeight,
  };
}
